import {
  Injectable,
  inject,
} from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpStatusCode,
} from '@angular/common/http';
import {
  lastValueFrom,
  Observable,
  throwError,
} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {IFlwApiParams} from '../../../../../models/flowable/flw-api-params';
import {ApiUrlService} from '../../../../infrastructure/api/core/api-url/api-url.service';
import {Observe} from '../../../../../common/enums/observe.enum';
import {FlowableAuthService} from '../auth/flowable-auth.service';
import {IFlwResponse} from '../../../../../models/flowable/flw-response.model';
import {AlertService} from '../../../../../modules/alert/services/alert/alert.service';
import {Constants} from '../../../../../common/constants/constants';

@Injectable({
  providedIn: 'root',
})
export class FlowableApiService {

  private http = inject(HttpClient);

  private apiUrlService = inject(ApiUrlService);

  private alertService = inject(AlertService);

  private flowableAuthService = inject(FlowableAuthService);

  /**
   * Returns a success response object with the provided data, or default values if not provided.
   * @param response - The response object to extract data from.
   * @returns A success response object with the provided data, or default values if not provided.
   */
  private getSuccessResponse(response?: any): IFlwResponse {
    return {
      code: response?.status || HttpStatusCode.Ok,
      message: response?.message || null,
      data: response?.data || null,
      result: true,
    };
  }

  /**
   * Generate error response object
   * @param errorResponse - Optional error response object
   * @returns IFlwResponse - The error response object
   */
  private getErrorResponse(errorResponse?: any): IFlwResponse {
    return {
      code: errorResponse?.status || null,
      message: errorResponse?.error?.message || null,
      data: errorResponse?.data || null,
      result: false,
    };
  }

  /**
   * Method: Returns the headers for the API request.
   * @returns {Object} - The headers object.
   */
  getHeaders(): {headers: HttpHeaders} {
    return this.flowableAuthService.httpOptionsWithBasicAuth();
  }

  /**
   * Method: Generates a Flowable URL for API methods.
   * @param url - The URL path to be appended to the base URL.
   * @returns The complete URL for the API method.
   */
  public getFlowableUrl(url: string): string {
    // Get the base URL from the API service
    const baseUrl = (window as any)?.flowable?.endpoints?.baseUrl || this.apiUrlService.sngFlwWorkApiUrl;
    // If a URL is provided, append it to the base URL; otherwise, return the base URL
    return url ? `${baseUrl}/${url}` : baseUrl;
  }

  /**
   * Method: Get flowable params for api methods
   * @param params
   */
  public getFlowableParams(params: IFlwApiParams[]): HttpParams {
    let httpParams = new HttpParams();
    params.forEach(item => {
      httpParams = httpParams.append(item.param, item.value);
    });
    return httpParams;
  }

  /**
   * Method: Generates a query string from the provided Flowable API parameters.
   * @param queryParams - The Flowable API parameters to generate a query string from.
   * @returns A query string that can be appended to a URL.
   */
  public getFlowableQueryParams(queryParams: IFlwApiParams[]): string {
    const queryString: string = queryParams
      .map(param => `${encodeURIComponent(param.param)}=${encodeURIComponent(param.value)}`)
      .join(Constants.AMPERSAND);
    return queryString ? Constants.QUESTION_MARK + queryString : '';
  }

  /**
   * Method: Makes a GET request with Flowable API by url and params.
   * @param url - the URL to make the GET request to
   * @param params - optional HTTP parameters
   * @param customError - Optional flag to indicate whether to handle the error with custom logic
   */
  public async flwGetRequest(url: string, params?: HttpParams, customError?: boolean): Promise<any> {
    const headers = params ? {...this.getHeaders(), params} : this.getHeaders();
    try {
      const response: any = await lastValueFrom(this.http.get(url, headers));
      return response || this.getSuccessResponse();
    } catch (error: any) {
      if (!customError) {
        this.flwError(error);
      }
      return this.getErrorResponse();
    }
  }

  /**
   * Wrapper function for making a GET request with Flowable API
   * @param url - the URL to make the GET request to
   * @param params - optional HTTP parameters
   * @param customError - Optional flag to indicate whether to handle the error with custom logic
   * @returns a Promise that resolves with the response data, or null if there's an error
   */
  public async flwGetRequestWrapper(url: string, params?: HttpParams, customError?: boolean): Promise<any> {
    const headers = params ? {...this.getHeaders(), params} : this.getHeaders();
    try {
      const response: any = await lastValueFrom(this.http.get(this.getFlowableUrl(url), headers));
      return response || this.getSuccessResponse();
    } catch (error: any) {
      if (!customError) {
        this.flwError(error);
      }
      return this.getErrorResponse();
    }
  }

  /**
   * Wrapper function for making a POST request with Flowable API to a given URL with optional body and parameters
   * @param url - The URL to make the POST request to
   * @param body - The body of the POST request
   * @param customError - Optional flag to indicate whether to handle the error with custom logic
   * @param params - Optional HTTP parameters
   * @returns A promise that resolves to the response of the POST request or an error
   */
  public async flwPostRequestWrapper(
    url: string,
    body: any,
    customError?: boolean,
    params?: HttpParams,
  ): Promise<any> {
    const headers = params ? {...this.getHeaders(), params} : this.getHeaders();
    try {
      const response: any = await lastValueFrom(this.http.post(this.getFlowableUrl(url), body, headers));
      return response || this.getSuccessResponse();
    } catch (error: any) {
      if (!customError) {
        this.flwError(error);
      }
      return this.getErrorResponse(error);
    }
  }

  /**
   * Wrapper function for making a PUT request with Flowable API to a specific URL with optional body and parameters.
   * @param url - The URL for the PUT request.
   * @param body - The body of the PUT request.
   * @param params - Optional HTTP parameters for the request.
   * @returns An observable of the response from the PUT request.
   */
  public flwPutRequestWrapper(url: string, body: any, params?: HttpParams): Observable<any> {
    const headers = params ? {...this.getHeaders(), params} : this.getHeaders();
    return this.http.put(this.getFlowableUrl(url), body, headers).pipe(
      catchError(error => {
        this.flwError(error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Wrapper function for making a PUT request with Flowable API to a specific URL with optional body and parameters.
   * @param url - The URL for the PUT request.
   * @param body - The body of the PUT request.
   * @param customError - Optional flag to indicate whether to handle the error with custom logic.
   * @param params - Optional HTTP parameters for the request.
   * @returns A promise that resolves to the response of the PUT request or an error.
   */
  public async flwPutPromiseRequestWrapper(
    url: string,
    body: any,
    customError?: boolean,
    params?: HttpParams,
  ): Promise<any> {
    const headers = params ? {...this.getHeaders(), params} : this.getHeaders();
    try {
      const response = await lastValueFrom(this.http.put(this.getFlowableUrl(url), body, headers));
      return response || this.getSuccessResponse();
    } catch (error) {
      if (!customError) {
        this.flwError(error);
      }
      return this.getErrorResponse(error);
    }
  }

  /**
   * Method: Send a DELETE request to Flowable with the authorization header only, without an error toast
   * @param url - Url path relative to the Flowable base url
   */
  public async flwDeleteWithAuthorization(url: string): Promise<void> {
    const headers = this.flowableAuthService.getFlowableAuthorizationHttpHeaders();
    await lastValueFrom(this.http.delete(this.getFlowableUrl(url), {headers}));
  }

  /**
   * Wrapper function for making a DELETE request with Flowable API
   * @param url - The URL for the delete request
   * @returns An observable of the delete request
   */
  public flwDeleteRequestWrapper(url: string): Observable<any> {
    const headers = {...this.getHeaders(), observe: Observe.RESPONSE};
    return this.http.delete(this.getFlowableUrl(url), headers).pipe(
      catchError(error => {
        this.flwError(error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Handles and displays errors using the alert service
   * @param errorResponse - The error to be handled
   */
  private flwError(errorResponse: any): void {
    if (errorResponse?.error?.exception) {
      this.alertService.error(errorResponse?.error.exception);
    }
    if (errorResponse?.error?.message) {
      this.alertService.error(errorResponse?.error.message);
    }
  }

}
